import { LevelType } from '../types/Level';
import { DraftLevel } from './types';

// What to search for when researching a song: the answer plus the artist the planner named.
export const songQuery = (draft: DraftLevel): string => {
  const name = (draft.sentence || '').trim();
  const artist = (draft.context || '').trim();
  return artist ? `${name} - ${artist}` : name;
};

// Part of every current song prompt; used to tell it apart from prompts written before the no-singer rules
const SONG_RULE_MARKER = 'אין לצייר זמר';

export const isCurrentSongPrompt = (imagePrompt: string): boolean => imagePrompt.indexOf(SONG_RULE_MARKER) !== -1;

const escapeForPattern = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Removes each name where it stands as a whole phrase (never from the middle of a longer word).
export const scrubNames = (text: string, names: string[]): string => {
  let out = text;
  names.map(name => name.trim()).filter(name => name.length > 0).forEach(name => {
    out = out.replace(new RegExp(`(^|[^\\u05D0-\\u05EAA-Za-z0-9])${escapeForPattern(name)}(?![\\u05D0-\\u05EAA-Za-z0-9])`, 'g'), '$1');
  });
  return out.replace(/[ \t]{2,}/g, ' ').replace(/ ([.,!?])/g, '$1').trim();
};

// The picture must hint at what the song is about, never at who sings it or what it is called:
// no title, no singer, nobody who looks like a real person.
export const songImagePrompt = (story: string, elements: string): string =>
  `צור תמונה ריבועית, מצויירת בסגנון איור לבחירתך, המתארת את הסצנה והאלמנטים הבאים:
${story}

אלמנטים בולטים:
${elements}

חוקים מחייבים:
- ללא טקסט בכלל: בלי אותיות, מילים, מספרים, כיתובים, שלטים עם כיתוב, סימן מים או לוגו.
- ${SONG_RULE_MARKER}, זמרת, להקה, מיקרופון, במה או הופעה.
- אין לצייר דמות שנראית כמו אדם אמיתי או מפורסם. אם יש דמויות, הן דמויות מאוירות כלליות ולא דיוקן של אדם מסוים.`;

// Asks Claude to design the visual riddle for a saying; the reply is the scene description.
export const sayingScenePrompt = (sentence: string): string =>
  `חשוב על רעיון יצירתי, חכם, ויזואלי ומתוחכם לתמונה (ללא שום טקסט בתוכה) שמתארת בצורה של חידה את הפתגם הבא: "${sentence}".
על התמונה לשלב באופן חכם גם את המשמעות הפיזית/המילולית של המילים בפתגם, וגם את המשמעות הסמויה והאמיתית שלו, כדי ליצור חידה מתוחכמת. (למשל עבור "יצא המרצע מן השק", שילוב של סוד גדול או אמת שיוצאים לאור, יחד עם אלמנטים של שק או חפץ חד).
תאר אך ורק את מה שצריך לצייר בתמונה בצורה ישירה, מפורטת וברורה, בלי הקדמות, בלי לכתוב "התמונה מראה" ובלי להסביר את המשמעות. רק תיאור של הסצנה.`;

export const sayingImagePrompt = (scene: string): string =>
  `צור תמונה ריבועית, ללא טקסט בכלל, בסגנון ציור אקראי שמתארת את הסצנה הבאה:
${scene.trim()}

התמונה מיועדת להיות חידה ויזואלית לפתגם. אסור לכלול טקסט, אותיות, מספרים או סמלים בתמונה.`;

// For pictures of real things, generated only when the user asks; these are often inaccurate.
export const realThingImagePrompt = (draft: DraftLevel): string => {
  const subject = draft.levelType === LevelType.WhereIsThisLocated
    ? `${draft.locationName || ''} ב${draft.sentence || ''}`
    : draft.sentence || '';
  const context = (draft.context || '').trim();
  return `צור תמונה ריבועית, ריאליסטית ככל האפשר כמו צילום, ללא טקסט, ללא כיתוב, ללא סימן מים וללא לוגו, של: ${subject.trim()}.${context ? `\n${context}` : ''}`;
};

export const INSPECTION_SCHEMA: Record<string, unknown> = {
  type: 'object',
  additionalProperties: false,
  required: ['containsText', 'matchesBrief', 'showsPerformer', 'notes'],
  properties: {
    containsText: { type: 'boolean' },
    matchesBrief: { type: 'boolean' },
    showsPerformer: { type: 'boolean' },
    notes: { type: 'string' },
  },
};

export const buildInspectionPrompt = (imagePrompt: string): string =>
  `You are checking a picture for a Hebrew puzzle game. The player must work out the answer from the picture alone, so the picture must not contain any writing.

The picture was meant to show this (the brief is in Hebrew):
${imagePrompt}

Look at the picture and report:
- "containsText": true if any letters, words, numbers, captions, signs with writing, watermarks or logos are visible anywhere, in any language. Decorative shapes that are not writing do not count.
- "matchesBrief": true if the picture clearly shows the main subject or scene of the brief. Style differences do not matter.
- "showsPerformer": true if the picture shows a singer, a band or a performance (someone singing, holding a microphone, playing on a stage), or a realistic portrait that looks like a specific real person rather than a generic illustrated character.
- "notes": one short English sentence describing the problem if any check fails, otherwise an empty string.`;

export interface InspectionVerdict {
  ok: boolean;
  problems: string[];
}

// forbidPerformer is set for song pictures, where showing the singer would give the answer away.
export const parseInspection = (json: unknown, forbidPerformer = false): InspectionVerdict => {
  const reply = json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  const unclear =
    typeof reply.containsText !== 'boolean' ||
    typeof reply.matchesBrief !== 'boolean' ||
    (forbidPerformer && typeof reply.showsPerformer !== 'boolean');
  if (unclear) {
    return { ok: false, problems: ['the check gave no clear answer'] };
  }

  const note = typeof reply.notes === 'string' && reply.notes.trim() ? ` (${reply.notes.trim()})` : '';
  const problems: string[] = [];
  if (reply.containsText) problems.push('contains text');
  if (forbidPerformer && reply.showsPerformer) problems.push('shows a singer, a performance or a real-looking person');
  if (!reply.matchesBrief) problems.push('does not match the intended scene');

  // The note explains the first problem found
  if (problems.length > 0) problems[0] += note;
  return { ok: problems.length === 0, problems };
};
